// ---------------------------------------------------------------------------
// ProgressDrawerV3 — drawer "Progresso da modelagem".
// ---------------------------------------------------------------------------
// Estrutura (de cima para baixo):
//
//   1) Header escuro com strip do cliente + titulo + seletor de mes +
//      botao fechar (``ProgressDrawerHeader``).
//   2) Cockpit fixo: KPIs (4) + barra de progresso multi-segmento
//      (``CockpitStrip``). Sempre visivel — decisao da sub-leva A.
//   3) Body com rail (190px) + pane ativa.
//      Rail:  Calculos · Pendencias · Linha do tempo
//      Panes: ``TimelinePane`` · ``ActionsPane`` · ``BlocksPane``
//
// O chassi (overlay/drawer/transition) reusa diretamente as classes
// do ``inputs_v3_components/drawer/styles.js`` para manter paridade
// visual e geometrica com o ``InputDrawerV3`` (drawer "gemeo").
//
// Sub-leva A: tudo mock no nivel das panes. As props que ficaram
// expostas (``actions``, ``events``, ``blocks``, ``blockLogs``,
// ``cockpit*``) ja preveem o ponto de plug-in para o backend que vai
// chegar nas sub-levas B/C — o componente nao precisa ser re-escrito
// quando os dados reais aparecerem.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import FlashOn from "@material-ui/icons/FlashOn";
import AssignmentLate from "@material-ui/icons/AssignmentLate";
import AccountTree from "@material-ui/icons/AccountTree";
import Build from "@material-ui/icons/Build";

import useDrawerStyles from "../drawer/styles";
import useThemeColors from "../../../../hooks/useThemeColors";
import DrawerRail from "../drawer/DrawerRail";

import ProgressDrawerHeader from "./ProgressDrawerHeader";
import CockpitStrip from "./CockpitStrip";
import TimelinePane from "./panes/TimelinePane";
import ActionsPane from "./panes/ActionsPane";
import { countPendingFromActionsCount } from "./panes/actionsPaneMatrix";
import BlocksPane from "./panes/BlocksPane";
import MonthAdminActionsPane from "./panes/MonthAdminActionsPane";

// Itens do rail. Mantidos em uma unica seccao logica ("trail") porque o
// drawer inteiro e mes-scoped. NOTA: o DrawerRail (apos a rodada
// 2026-04-28 do InputDrawer) renderiza apenas grupos cujas chaves
// existam em GROUP_TITLES (hoje: "trail", "config", "adm") — usar "now"
// aqui faria o rail sumir.
//
// Leva 97: grupo "adm" + tab "actions_adm" — só renderiza para DEV
// (gate replica o ``@requires('dev_only')`` no backend de
// ``/reset-month/``).
const buildRailItems = ({ pendingCount, userRole }) => {
  const items = [
    { id: "blocks", label: "Cálculos", icon: AccountTree, group: "trail" },
    {
      id: "actions",
      label: "Pendências",
      icon: AssignmentLate,
      group: "trail",
      badgeCount: pendingCount || 0,
    },
    { id: "timeline", label: "Linha do tempo", icon: FlashOn, group: "trail" },
  ];
  if (userRole === "DEV") {
    items.push({
      id: "actions_adm",
      label: "Ações",
      icon: Build,
      group: "adm",
    });
  }
  return items;
};

const ProgressDrawerV3 = ({
  open,
  onClose,
  monthlyStatus,
  selectedMonthIndex,
  onSelectMonth,
  // Cockpit (props mock por enquanto, viram do backend nas levas B/C)
  monthsImplementation,
  inputsSent,
  actionsCount,
  // Panes
  events,
  actions,
  blocks,
  blockLogs,
  onOpenInput,
  // Callback de paridade visual (leva 93): quando o
  // ``BlocksPane`` recebe um payload de ``/blocks/`` com
  // ``monthStatus`` canonico, dispara este callback para a view
  // ``InputsV3`` atualizar o badge do topo no MESMO tick em que os
  // tiles dos blocos transicionam. Garante que badge e Cálculos
  // nunca apresentam status divergente.
  onMonthStatusChange,
  // Endpoint consolidado (leva 80). Quando presente, sobrepoe os
  // mocks acima — props individuais ficam como fallback p/
  // chamadores legados (storybook, sandbox).
  progressOverview,
  progressOverviewLoading = false,
  // Leva 97: contexto para aba "ADM > Ações" (reset do mês).
  appName,
  userRole,
  onMonthReset,
  // Onda 2.0 (mai/2026): payload completo da secao ``blocks`` vinda
  // do hook unico ``useInputsStateV3`` (parent ``InputsV3.js``).
  // Encaminhado para o ``BlocksPane`` que, em modo override, NAO
  // dispara mais o ``useBlocksV3`` proprio. Ver
  // ``inputs_v3_components/useInputsStateV3.js`` e a doc do
  // endpoint consolidado em ``api/views/inputs_v3_state.py``.
  blocksOverride,
  // Onda 2.5 (mai/2026): aba inicial ao abrir o drawer. Usado pelo
  // ``InputsV3GlobalTopBar`` para abrir direto na aba "Pendências"
  // quando o usuario clica no tile correspondente. Default "blocks"
  // mantem o comportamento legado (botao "Cálculos" da V3 antiga).
  initialTab = "blocks",
}) => {
  // Resolve a fonte de cada bloco do drawer. Backend > prop direta >
  // mock (default da prop).
  const resolvedMonthsImplementation =
    progressOverview?.months_implementation || monthsImplementation;
  // ``actions_count`` (cru) e' expandido por responsavel — uma decisao
  // com 3 responsaveis vira ``decisions=3``. Esse formato e' o que a
  // matriz pessoa x tipo do ``ActionsPane`` precisa.
  //
  // ``actions_count_unique`` conta cada acao UMA vez (semantica de
  // "quantas pendencias distintas existem"). E' o que o tile no topo
  // da view principal (``InputsV3GlobalTopBar``) e o badge desta tab
  // mostram — sem essa distincao, abrir o drawer com "47 pendencias"
  // no topo via virava um KPI/pip de "47" tambem aqui dentro, com a
  // matriz exibindo as mesmas 38 entradas distintas. Mantemos as duas
  // referencias para que cada consumidor use a semantica certa.
  const resolvedActionsCount = progressOverview?.actions_count || actionsCount;
  const resolvedActionsCountUnique =
    progressOverview?.actions_count_unique || resolvedActionsCount;
  // Onda 5.x (2026-05-27): contadores X/Y dos calculos do mes — backend
  // expoe via ``queue.done`` / ``queue.total`` (ver inputs_v3.py). KPI
  // "Cálculos" do CockpitStrip consome esses dois numeros. Os antigos
  // ``queueCount`` / ``queueWaiting`` (running/queued/waiting_inputs)
  // foram aposentados no cockpit; ainda chegam no payload do backend
  // mas nao sao mais expostos aqui — caso volte a precisar, ler de
  // ``progressOverview.queue.processing|queued|waiting_inputs``.
  const resolvedBlocksDone = progressOverview?.queue?.done || 0;
  const resolvedBlocksTotal = progressOverview?.queue?.total || 0;
  // ``progressOverview.events`` veio do endpoint consolidado e e'
  // job-based (1 por ``ProcessingJobV3`` do mes — STR/CSL/BLOCK).
  // Esse formato perde dois tipos de entrega que a UI principal
  // considera "entregue":
  //   - auto_reuse (monthly/singleton com ``reuse_auto=True`` que
  //     replica do mes anterior sem rodar STR → sem job, sem evento);
  //   - cobertura por entrega anterior (singleton / yearly /
  //     monthly+reuse_auto herdados via ``has_prior_success`` →
  //     entrega real em outro mes, nao ha job neste mes).
  // O endpoint dedicado ``InputsV3TimelineView`` ja cobre ambos via
  // ``InputControlV3.delivered_at`` + evento sintetico ``inherited``.
  // Aqui forcamos o ``TimelinePane`` a sempre buscar do dedicado
  // (``events={undefined}`` -> remote fetch). Storybook/sandbox
  // continuam podendo injetar ``events`` direto pela prop deste
  // componente — ``events`` permanece no contrato como override
  // explicito.
  const resolvedEvents = events;
  const resolvedActions = progressOverview?.actions || actions;
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const [activeTab, setActiveTab] = useState(initialTab || "blocks");

  // Default em ``initialTab`` toda vez que abrir. Mesma logica do
  // InputDrawerV3 (que reseta para "Este mes" ao reabrir). A
  // ``GlobalTopBar`` usa esse contrato para abrir direto em
  // "actions" quando o usuario clica no tile "Pendências".
  useEffect(() => {
    if (open) setActiveTab(initialTab || "blocks");
  }, [open, initialTab]);

  // ESC fecha o drawer.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Badge da aba "Pendencias" deve refletir a MESMA contagem do tile
  // de pendencias da view principal — count de acoes DISTINTAS, nao
  // expandido por responsavel. ``pendingFromList`` (length da lista
  // ``actions``) e' expandido e por isso ficou banido daqui. Usamos o
  // ``actions_count_unique`` direto (com fallback pro cru ja embutido
  // em ``resolvedActionsCountUnique``).
  const pendingCount =
    countPendingFromActionsCount(resolvedActionsCountUnique) || undefined;

  const items = buildRailItems({ pendingCount, userRole });

  // Contexto de periodos para o BlockLogViewer (sub-leva A — derivado
  // do monthlyStatus). monthlyStatus[0] = primeiro mes da impl;
  // ultimo item = mes mais recente disponivel; mes selecionado = "atual"
  // da comparacao.
  const periodCtx = useMemo(() => {
    const arr = monthlyStatus || [];
    if (!arr.length) return undefined;
    const sel =
      typeof selectedMonthIndex === "number" && arr[selectedMonthIndex]
        ? arr[selectedMonthIndex]
        : arr[arr.length - 1];
    const first = arr[0];
    const last = arr[arr.length - 1];
    if (!sel || !first || !last) return undefined;
    return {
      currentYear: sel.year,
      currentMonth: sel.month,
      firstYear: first.year,
      firstMonth: first.month,
      lastYear: last.year,
      lastMonth: last.month,
    };
  }, [monthlyStatus, selectedMonthIndex]);

  return (
    <>
      <div
        className={`${classes.overlay} ${open ? classes.overlayOpen : ""}`}
        style={{ zIndex: 1288 }}
        onClick={onClose}
      />
      <aside
        className={`${classes.drawer} ${open ? classes.drawerOpen : ""}`}
        style={{ zIndex: 1289 }}
        role="dialog"
        aria-label="Progresso da modelagem"
      >
        <ProgressDrawerHeader
          monthlyStatus={monthlyStatus}
          selectedMonthIndex={selectedMonthIndex}
          onSelectMonth={onSelectMonth}
          onClose={onClose}
        />

        <CockpitStrip
          monthsImplementation={resolvedMonthsImplementation}
          inputsSent={inputsSent}
          actionsCount={resolvedActionsCountUnique}
          blocksDone={resolvedBlocksDone}
          blocksTotal={resolvedBlocksTotal}
        />

        <div className={classes.drawerBody}>
          <DrawerRail
            items={items}
            activeId={activeTab}
            onSelect={setActiveTab}
          />
          <div className={classes.content}>
            {activeTab === "timeline" && (
              <TimelinePane
                // Fase 5.2: ``events`` continua aceito (storybook/test
                // injetam dados explicitos), mas o caminho de producao
                // passa a buscar do endpoint dedicado
                // ``InputsV3TimelineView`` quando recebemos
                // ``year``/``month`` (do ``periodCtx``) e nao temos
                // ``resolvedEvents`` populado pelo
                // ``progressOverview``.
                events={
                  resolvedEvents && resolvedEvents.length > 0
                    ? resolvedEvents
                    : undefined
                }
                year={periodCtx?.currentYear}
                month={periodCtx?.currentMonth}
              />
            )}
            {activeTab === "actions" && (
              <ActionsPane
                items={resolvedActions}
                overviewLoading={progressOverviewLoading}
              />
            )}
            {activeTab === "blocks" && (
              <BlocksPane
                blocks={blocks}
                blockLogs={blockLogs}
                periodCtx={periodCtx}
                onOpenInput={onOpenInput}
                onMonthStatusChange={onMonthStatusChange}
                monthStatusHint={progressOverview?.month_status || null}
                blocksOverride={blocksOverride}
              />
            )}
            {activeTab === "actions_adm" && (
              <MonthAdminActionsPane
                appName={appName}
                year={periodCtx?.currentYear}
                month={periodCtx?.currentMonth}
                userRole={userRole}
                onMonthReset={onMonthReset}
              />
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

ProgressDrawerV3.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  monthlyStatus: PropTypes.array,
  selectedMonthIndex: PropTypes.number,
  onSelectMonth: PropTypes.func,
  monthsImplementation: PropTypes.object,
  inputsSent: PropTypes.object,
  actionsCount: PropTypes.object,
  events: PropTypes.array,
  actions: PropTypes.array,
  blocks: PropTypes.array,
  blockLogs: PropTypes.object,
  onOpenInput: PropTypes.func,
  onMonthStatusChange: PropTypes.func,
  progressOverview: PropTypes.object,
  progressOverviewLoading: PropTypes.bool,
  appName: PropTypes.string,
  userRole: PropTypes.string,
  onMonthReset: PropTypes.func,
  initialTab: PropTypes.oneOf(["blocks", "actions", "timeline", "actions_adm"]),
  blocksOverride: PropTypes.shape({
    blocks: PropTypes.array,
    summary: PropTypes.object,
    monthStatus: PropTypes.string,
    monthStatusReason: PropTypes.string,
    monthStatusActive: PropTypes.object,
    snapshotSource: PropTypes.object,
    userIsDev: PropTypes.bool,
    refetch: PropTypes.func,
  }),
};

export default ProgressDrawerV3;
